import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  Calendar, 
  Clock, 
  Users, 
  Trophy,
  CheckCircle,
  X,
  Eye
} from 'lucide-react';
import { useUserRole } from '@/hooks/useUserRole';
import { GameStateService } from '@/services/gameStateService';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import { toast } from 'sonner';

interface ScorekeeperApplication {
  id: string;
  game_id: string;
  scorekeeper_id: string;
  applied_at: string;
  status: 'pending' | 'approved' | 'rejected';
  approved_by?: string;
  approved_at?: string;
  rejection_reason?: string;
  game?: any;
  scorekeeper?: any;
}

export const ScorekeeperApplications: React.FC = () => {
  const { permissions, userId } = useUserRole();
  const [applications, setApplications] = useState<ScorekeeperApplication[]>([]);
  const [availableGames, setAvailableGames] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [userId, permissions]);

  const loadData = async () => {
    if (!userId) return;

    setLoading(true);
    try {
      if (permissions.role === 'scorekeeper') {
        await loadScorekeeperView();
      } else if (permissions.isCoach || permissions.isTeamOwner) {
        await loadTeamManagerView();
      }
    } catch (error) {
      console.error('Error loading scorekeeper data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadScorekeeperView = async () => {
    // Load my applications
    const { data: myApplications } = await supabase
      .from('scorekeeper_applications')
      .select(`
        *,
        game:games(
          *,
          home_team:teams!games_home_team_id_fkey(team_id, team_name),
          away_team:teams!games_away_team_id_fkey(team_id, team_name)
        )
      `)
      .eq('scorekeeper_id', userId)
      .order('applied_at', { ascending: false });

    setApplications((myApplications || []) as ScorekeeperApplication[]);

    // Load available games I can apply to
    const { data: games } = await supabase
      .from('games')
      .select(`
        *,
        home_team:teams!games_home_team_id_fkey(team_id, team_name),
        away_team:teams!games_away_team_id_fkey(team_id, team_name)
      `)
      .eq('game_status', 'approved')
      .gte('game_date', new Date().toISOString().split('T')[0])
      .not('id', 'in', 
        supabase
          .from('scorekeeper_applications')
          .select('game_id')
          .eq('scorekeeper_id', userId)
      );

    setAvailableGames(games || []);
  };

  const loadTeamManagerView = async () => {
    // Load applications for my games
    const { data: gameApplications } = await supabase
      .from('scorekeeper_applications')
      .select(`
        *,
        game:games(
          *,
          home_team:teams!games_home_team_id_fkey(team_id, team_name),
          away_team:teams!games_away_team_id_fkey(team_id, team_name)
        ),
        scorekeeper:profiles(id, username, first_name, last_name)
      `)
      .eq('status', 'pending')
      .or(`game_id.in.(SELECT id FROM games WHERE created_by = '${userId}' OR home_team_id IN (SELECT team_id FROM teams WHERE created_by = '${userId}') OR away_team_id IN (SELECT team_id FROM teams WHERE created_by = '${userId}'))`);

    setApplications((gameApplications || []) as ScorekeeperApplication[]);
  };

  const handleApplyForGame = async (gameId: string) => {
    const success = await GameStateService.applyForGame(gameId, userId!);
    if (success) {
      loadData();
    }
  };

  const handleApproveApplication = async (applicationId: string) => {
    const success = await GameStateService.approveScorekeeper(applicationId, userId!);
    if (success) {
      loadData();
    }
  };

  const handleRejectApplication = async (applicationId: string) => {
    try {
      const { error } = await supabase
        .from('scorekeeper_applications')
        .update({
          status: 'rejected',
          rejection_reason: 'Rejected by team manager'
        })
        .eq('id', applicationId);

      if (error) throw error;

      toast.success('Application rejected');
      loadData();
    } catch (error) {
      console.error('Error rejecting application:', error);
      toast.error('Failed to reject application');
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge variant="outline" className="bg-yellow-500/10 text-yellow-500 border-yellow-500">Pending</Badge>;
      case 'approved':
        return <Badge variant="outline" className="bg-green-500/10 text-green-500 border-green-500">Approved</Badge>;
      case 'rejected':
        return <Badge variant="outline" className="bg-red-500/10 text-red-500 border-red-500">Rejected</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-gray-400">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">
            {permissions.role === 'scorekeeper' ? 'Scoring Opportunities' : 'Scorekeeper Applications'}
          </h2>
          <p className="text-gray-400">
            {permissions.role === 'scorekeeper' 
              ? 'Apply to score games and manage your assignments'
              : 'Review and approve scorekeeper applications for your games'
            }
          </p>
        </div>
        <Button onClick={loadData} variant="outline" size="sm">
          Refresh
        </Button>
      </div>

      {permissions.role === 'scorekeeper' && (
        <>
          {/* Available Games */}
          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-white">Available Games</h3>
            {availableGames.length === 0 ? (
              <Card className="bg-gray-800 border-gray-700">
                <CardContent className="p-6 text-center">
                  <Trophy className="h-12 w-12 text-gray-500 mx-auto mb-4" />
                  <p className="text-gray-400 text-lg">No games available</p>
                  <p className="text-gray-500 text-sm mt-2">Check back later for new scoring opportunities</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {availableGames.map((game) => (
                  <Card key={game.id} className="bg-gray-800 border-gray-700">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <h4 className="font-medium text-white mb-2">{game.game_name}</h4>
                          <div className="grid grid-cols-2 gap-4 text-sm text-gray-400 mb-2">
                            <div className="flex items-center gap-2">
                              <Calendar className="h-4 w-4" />
                              {format(new Date(game.game_date), 'MMM dd, yyyy')}
                            </div>
                            <div className="flex items-center gap-2">
                              <Clock className="h-4 w-4" />
                              {game.game_time}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-gray-400">
                            <Users className="h-4 w-4" />
                            {game.home_team?.team_name || 'TBD'} vs {game.away_team?.team_name || 'TBD'}
                          </div>
                        </div>
                        <Button
                          onClick={() => handleApplyForGame(game.id)}
                          className="bg-blue-600 hover:bg-blue-700"
                        >
                          Apply to Score
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>

          {/* My Applications */}
          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-white">My Applications</h3>
            {applications.length === 0 ? (
              <Card className="bg-gray-800 border-gray-700">
                <CardContent className="p-6 text-center">
                  <CheckCircle className="h-12 w-12 text-gray-500 mx-auto mb-4" />
                  <p className="text-gray-400 text-lg">No applications yet</p>
                  <p className="text-gray-500 text-sm mt-2">Apply to games above to get started</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {applications.map((application) => (
                  <Card key={application.id} className="bg-gray-800 border-gray-700">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h4 className="font-medium text-white">{application.game?.game_name}</h4>
                            {getStatusBadge(application.status)}
                          </div>
                          <div className="grid grid-cols-2 gap-4 text-sm text-gray-400 mb-2">
                            <div className="flex items-center gap-2">
                              <Calendar className="h-4 w-4" />
                              {format(new Date(application.game?.game_date), 'MMM dd, yyyy')}
                            </div>
                            <div className="flex items-center gap-2">
                              <Clock className="h-4 w-4" />
                              Applied: {format(new Date(application.applied_at), 'MMM dd')}
                            </div>
                          </div>
                          {application.status === 'approved' && (
                            <div className="text-sm text-green-400">
                              Approved on {format(new Date(application.approved_at!), 'MMM dd, yyyy')}
                            </div>
                          )}
                          {application.status === 'rejected' && application.rejection_reason && (
                            <div className="text-sm text-red-400">
                              Rejected: {application.rejection_reason}
                            </div>
                          )}
                        </div>
                        {application.status === 'approved' && (
                          <Button
                            onClick={() => window.open(`/games/${application.game_id}/score`, '_blank')}
                            variant="outline"
                          >
                            <Trophy className="h-4 w-4 mr-2" />
                            Score Game
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {(permissions.isCoach || permissions.isTeamOwner) && (
        <div className="space-y-4">
          <h3 className="text-xl font-semibold text-white">Pending Applications</h3>
          {applications.length === 0 ? (
            <Card className="bg-gray-800 border-gray-700">
              <CardContent className="p-6 text-center">
                <Users className="h-12 w-12 text-gray-500 mx-auto mb-4" />
                <p className="text-gray-400 text-lg">No pending applications</p>
                <p className="text-gray-500 text-sm mt-2">Scorekeeper applications will appear here for review</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {applications.map((application) => (
                <Card key={application.id} className="bg-gray-800 border-gray-700">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <h4 className="font-medium text-white mb-2">{application.game?.game_name}</h4>
                        <div className="text-sm text-gray-400 mb-2">
                          Scorekeeper: {application.scorekeeper?.first_name} {application.scorekeeper?.last_name} 
                          ({application.scorekeeper?.username})
                        </div>
                        <div className="grid grid-cols-2 gap-4 text-sm text-gray-400">
                          <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4" />
                            {format(new Date(application.game?.game_date), 'MMM dd, yyyy')}
                          </div>
                          <div className="flex items-center gap-2">
                            <Clock className="h-4 w-4" />
                            Applied: {format(new Date(application.applied_at), 'MMM dd')}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => handleApproveApplication(application.id)}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          <CheckCircle className="h-4 w-4 mr-1" />
                          Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleRejectApplication(application.id)}
                        >
                          <X className="h-4 w-4 mr-1" />
                          Reject
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};